<template>
  <div v-if="dataset !== null" class="dataset-stats__page">
    <StatsOverview
      :dataset="dataset"
      :boxSize="boxSize"
      @new-set="handleNewSetSelected"
    />

    <div class="dataset-stats__main scrollbar">
      <template v-if="isNotEmpty && !fetching">
        <div class="row-40">
          <div class="chart col-40">
            <LabelsPieChart
              :name="'Label Count'"
              :dataArray="labelCountData"
            />
          </div>
          <div class="chart col-60">
            <ConfidenceHistogram
              :name="'Confidence Histogram'"
              :dataArray="histogramData"
            />
          </div>
        </div>
        <div class="row-40">
          <div class="chart col-100">
            <BoundingBoxSizeHistogram
              :name="'Annotation Size Histogram'"
              :dataArray="bboxSizeHistogram"
              @custom-range="(range) => getBBoxSizeHistogram(range)"
            />
          </div>
        </div>
        <div class="row-60">
          <div class="chart col-50">
            <LabelLocationMap
              :name="'Annotation Location Density'"
              :dataset="dataset"
              :labelList="labelList"
              :dataArray="locationMap"
              @new-location-label="handleNewLabelSelected"
            />
          </div>
          <div class="chart col-50">
            <BoundingBoxDisplay
              :name="'Bounding Box Visualization'"
              :dataset="dataset"
              :labelList="labelList"
              :annotationList="filteredAnnotationList"
              @new-visualizer-label="handleNewLabelVisualizerSelected"
            />
          </div>
        </div>
      </template>
      <template v-else-if="isNotEmpty && fetching">
        <div
          class="empty-state"
        >
          <svg width="60%" height="60%">
            <use href="@/assets/img/empty.svg#emptyStateSVG" />
          </svg>
          <div class="empty-state__message">
            <h3>Fetching analytics.</h3>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="empty-state"
        >
          <svg width="60%" height="60%">
            <use href="@/assets/img/empty.svg#emptyStateSVG" />
          </svg>
          <div class="empty-state__message">
            <h3>Please select an annotation set.</h3>
          </div>
        </div>
      </template>
    </div>
  </div>
  <div
    v-else
    class="empty-state"
  >
    <svg width="60%" height="60%">
      <use href="@/assets/img/empty.svg#emptyStateSVG" />
    </svg>
    <div class="empty-state__message">
      <h3>Please select a dataset.</h3>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRoute } from 'vue-router';
import { decodeID } from '@/assets/js/utils';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import StatsOverview from '@/components/DatasetComponent/DatasetStats/StatsOverview.vue';
import ConfidenceHistogram from '@/components/DatasetComponent/DatasetStats/ConfidenceHistogram.vue';
import LabelsPieChart from '@/components/DatasetComponent/DatasetStats/LabelsPieChart.vue';
import LabelLocationMap from '@/components/DatasetComponent/DatasetStats/LabelLocationMap.vue';
import BoundingBoxDisplay from '@/components/DatasetComponent/DatasetStats/BoundingBoxDisplay.vue';
import BoundingBoxSizeHistogram from '@/components/DatasetComponent/DatasetStats/BoundingBoxSizeHistogram.vue';

const store = useStore();
const route = useRoute();

// Data
const dataConnect = ref(new DatastoreConnect(store.state.enterpriseServerUrl));
const dataset = ref(null);
const set = ref(null);
const boxSize = ref(null);
const labelCountData = ref(null);
const histogramData = ref(null);
const locationMap = ref(null);
const fullAnnotationList = ref([]);
const labelList = ref([]);
const labelLocation = ref(null);
const labelVisualizer = ref(null);
const bboxSizeHistogram = ref(null);
const fetching = ref(false);

// Computed
const currentProject = computed(() => store.state.projects.currentProject);
const isNotEmpty = computed(() => labelCountData.value !== null && histogramData.value !== null);
const filteredAnnotationList = computed(() => {
  if (labelVisualizer.value && labelVisualizer.value.index !== -1) {
    return fullAnnotationList.value.filter((e) => e.label_id === labelVisualizer.value.id);
  } else {
    return fullAnnotationList.value;
  }
});

// Methods
const resetState = () => {
  set.value = null;
  labelCountData.value = null;
  histogramData.value = null;
  locationMap.value = null;
  boxSize.value = null;
  labelLocation.value = null;
  labelVisualizer.value = null;
};

const getAllStats = async () => {
  const promises = [];
  fetching.value = true;
  promises.push(getConfHist());
  promises.push(getLabelCount());
  promises.push(getMinMaxBox());
  promises.push(getAllAnnotations());
  promises.push(getBBoxSizeHistogram([0.0, 1.0]));

  await Promise.all(promises)
    .then(() => {
      fetching.value = false;
    })
    .catch((e) => {
      console.log(e);
      fetching.value = false;
    });
};

const getConfHist = async () => {
  await dataConnect.value.getDatasetStats({
    type: "dataset_histogram",
    dataset_id: dataset.value.id,
    annotation_sets: set.value.id,
  })
    .then((data) => {
      histogramData.value = data.result;
    })
    .catch((e) => console.log(e));
};

const getBBoxSizeHistogram = async (range) => {
  await dataConnect.value.getDatasetStats({
    dataset_id: dataset.value.id,
    type: "bbox_size_histogram",
    annotation_sets: set.value.id,
    lower: range[0],
    upper: range[1],
  })
    .then((data) => {
      bboxSizeHistogram.value = data.result;
      bboxSizeHistogram.value.annotation_set_name = set.value.name;
    })
    .catch((e) => console.log(e));
};

const getLabelCount = async () => {
  await dataConnect.value.getDatasetStats({
    type: "label_count",
    dataset_id: dataset.value.id,
    annotation_sets: set.value.id,
  })
    .then((data) => {
      labelCountData.value = data.result;
    })
    .catch((e) => console.log(e));
};

const getLocationMap = async (label) => {
  if (label) {
    await dataConnect.value.getDatasetStats({
      type: "location_map",
      dataset_id: dataset.value.id,
      annotation_sets: set.value.id,
      label_id: label.id,
    })
      .then((data) => {
        locationMap.value = data.result;
      })
      .catch((e) => console.log(e));
  }
};

const getMinMaxBox = async () => {
  await dataConnect.value.getDatasetStats({
    type: "box_sizes",
    dataset_id: dataset.value.id,
    annotation_sets: set.value.id,
  })
    .then((data) => {
      boxSize.value = data.result;
    })
    .catch((e) => console.log(e));
};

const getAllAnnotations = async () => {
  await dataConnect.value.getAnnotationsForAnnotationSets({
    annotation_set_id: set.value.id,
  })
    .then((data) => {
      fullAnnotationList.value = data.result;
    })
    .catch((e) => console.log(e));
};

const handleNewSetSelected = (s) => {
  if (s && Object.keys(s).length > 0) {
    set.value = s;
    getAllStats();
    getLocationMap(labelLocation.value);
  }
};

const handleNewLabelSelected = (l) => {
  labelLocation.value = l;
};

const handleNewLabelVisualizerSelected = (l) => {
  labelVisualizer.value = l;
};

// Watchers
watch(() => labelLocation.value, (l) => {
  if (l) {
    getLocationMap(l);
  }
});

// watch(() => dataset.value, () => {
//   resetState();
// });

// Lifecycle hooks
onMounted(async () => {
  // Get dataset UID from URL and fetch dataset
  const datasetHex = route.params.datasetUID;
  if (datasetHex) {
    const uid = `ds-${datasetHex}`;
    const datasetID = decodeID(uid, 'ds');
    try {
      const response = await dataConnect.value.getDatasetById({
        dataset_id: datasetID,
        get_num_images: true,
        get_labels: true,
        get_num_labels: true,
        get_annotation_sets: true,
        get_num_annotation_sets: true,
        get_annotation_sets_with_counts: true,
        get_annotation_types: true,
        get_groups: true,
        get_docker_tasks: true,
      });
      if (response && response.result) {
        dataset.value = response.result;
        // Extract labels from the dataset object
        if (response.result.labels) {
          labelList.value = response.result.labels;
        }
      }
    } catch (error) {
      console.error('Error fetching dataset:', error);
    }
  }
});

// onUnmounted(() => {
//   store.commit('statistics/setDatasetID', dataset.value?.id);
//   store.commit('statistics/setAnnotationSet', set.value);
//   store.commit('statistics/setLabelLocation', labelLocation.value);
//   store.commit('statistics/setLabelVisualizer', labelVisualizer.value);
// });
</script>

<style lang="scss" scoped>
.dataset-stats {

  &__page {
    display: flex;
    flex-direction: row;
    flex: 1 1 auto;
    height: 100%;
  }

  &__main {
    height: calc(100% - 20px);
    width: 100%;
    margin: 10px;
    padding-top: 3px;
    padding-bottom: 2px;
    padding-left: 3px;
    padding-right: 10px;
    overflow-y: scroll;

    .row-40 {
      display: flex;
      flex-direction: row;
      height: 400px;
    }
    .row-60 {
      display: flex;
      flex-direction: row;
      height: 600px;
    }

    .col-40 {
      display: flex;
      flex-direction: column;
      width: 40%;
    }

    .col-50 {
      display: flex;
      flex-direction: column;
      width: 50%;
    }

    .col-60 {
      display: flex;
      flex-direction: column;
      width: 60%;
    }

    .col-100 {
      display: flex;
      flex-direction: column;
      width: 100%;
    }

    .chart + .chart {
      margin-left: 20px;
    }

  }

  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;

    span {
      font-weight: 700;
      font-size: 1.1rem;
      margin-right: 20px;
    }
  }
}

.add-task-icon:hover {
  @include themify() {
    color: themed('icon-color-primary');
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 100%;
  height: 100%;
}
</style>
